import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./NotFoundBlockComponent.css?inline";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * NotFoundBlockComponent — A leaf component for rendering a "not found" message in the chat.
 *
 * It renders the following structure:
 * <div class="chat-message__bubble chat-message__bubble--not-found">
 *   <span class="chat-message__text">I was not able to find such information. Please try something else.</span>
 * </div>
 */
export class NotFoundBlockComponent extends HTMLElement {
    static get observedAttributes(): string[] {
        return ["role"];
    }

    private static readonly NOT_FOUND_TEXT = "I was not able to find such information. Please try something else.";

    constructor() {
        super();
    }

    /**
     * Returns the primary text content for accessibility aggregation.
     */
    get accessibilityText(): string {
        return NotFoundBlockComponent.NOT_FOUND_TEXT;
    }

    /**
     * Called when the element is first connected to the document's DOM.
     * Initializes the internal structure if not already present.
     */
    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: "open" });
            shadow.adoptedStyleSheets = [sharedSheet, componentSheet];
            this.render();
        }
        this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
    }

    /**
     * Creates the internal DOM structure of the component.
     */
    private render(): void {
        const bubble = document.createElement("div");
        bubble.className = "chat-message__bubble chat-message__bubble--not-found";

        const textSpan = document.createElement("span");
        textSpan.className = "chat-message__text";
        textSpan.textContent = NotFoundBlockComponent.NOT_FOUND_TEXT;

        bubble.appendChild(textSpan);
        this.shadowRoot?.appendChild(bubble);
    }
}

/**
 * Register the custom element if it hasn't been registered yet.
 */
if (!customElements.get("jt-not-found-block")) {
    customElements.define("jt-not-found-block", NotFoundBlockComponent);
}
